import { Settings, CheckCircle, Clock, XCircle, AlertCircle, RefreshCw, Server, Network, Cpu } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ConfigComplianceSummary } from '../../services/dashboardSummary.service';

interface ComplianceDevice {
    id: string;
    name: string;
    status: string;
    deviceType?: 'edge' | 'connector' | 'mtge';
    dirtyReason?: string;
    lastError?: string;
}

interface ConfigComplianceCardProps {
    data: ConfigComplianceSummary | null;
    allDevices?: ComplianceDevice[];
    loading: boolean;
}

const StatusBadge = ({ status }: { status: string }) => {
    const config: Record<string, { bg: string; text: string; icon: typeof CheckCircle }> = {
        synced: { bg: 'bg-success-100', text: 'text-success-700', icon: CheckCircle },
        pending: { bg: 'bg-warning-100', text: 'text-warning-700', icon: Clock },
        applying: { bg: 'bg-info-100', text: 'text-info-700', icon: RefreshCw },
        failed: { bg: 'bg-danger-100', text: 'text-danger-700', icon: XCircle },
        unknown: { bg: 'bg-gray-100', text: 'text-gray-600', icon: AlertCircle },
    };

    const { bg, text, icon: Icon } = config[status] || config.unknown;

    return (
        <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-xs font-medium ${bg} ${text}`}>
            <Icon className="w-3 h-3" />
            <span className="capitalize">{status}</span>
        </span>
    );
};

export const ConfigComplianceCard = ({ data, allDevices, loading }: ConfigComplianceCardProps) => {
    if (loading) {
        return (
            <div className="card animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="flex gap-4 mb-4">
                    <div className="w-24 h-24 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!data || data.total === 0) {
        return (
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Configuration Compliance</h2>
                    <Settings className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Settings className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No configuration data available</p>
                    <p className="text-xs text-gray-400 mt-1">Configuration status will appear here</p>
                </div>
            </div>
        );
    }

    const total = data.total || 1;
    const syncedPercent = (data.synced / total) * 100;
    const pendingPercent = (data.pending / total) * 100;
    const failedPercent = (data.failed / total) * 100;
    const unknownPercent = (data.unknown / total) * 100;

    // Calculate circumference for donut chart
    const radius = 36;
    const circumference = 2 * Math.PI * radius;

    // Calculate stroke-dasharray for each segment
    const syncedDash = (syncedPercent / 100) * circumference;
    const pendingDash = (pendingPercent / 100) * circumference;
    const failedDash = (failedPercent / 100) * circumference;
    const unknownDash = (unknownPercent / 100) * circumference;

    // Calculate offsets for each segment
    const syncedOffset = 0;
    const pendingOffset = -syncedDash;
    const failedOffset = -(syncedDash + pendingDash);
    const unknownOffset = -(syncedDash + pendingDash + failedDash);

    return (
        <div className="card">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Configuration Compliance</h2>
                <div className="flex items-center space-x-3 text-xs font-medium">
                    <Link to="/edges" className="text-primary-600 hover:text-primary-700 flex items-center space-x-1">
                        <Server className="w-3 h-3" /><span>Edges</span>
                    </Link>
                    <Link to="/mtges" className="text-primary-600 hover:text-primary-700 flex items-center space-x-1">
                        <Network className="w-3 h-3" /><span>Gateways</span>
                    </Link>
                    <Link to="/connectors" className="text-primary-600 hover:text-primary-700 flex items-center space-x-1">
                        <Cpu className="w-3 h-3" /><span>Connectors</span>
                    </Link>
                </div>
            </div>

            <div className="flex gap-6 mb-4">
                {/* Donut Chart */}
                <div className="relative w-24 h-24 flex-shrink-0">
                    <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 80 80">
                        {/* Background circle */}
                        <circle
                            cx="40"
                            cy="40"
                            r={radius}
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="8"
                        />
                        {/* Synced segment (green) */}
                        {syncedPercent > 0 && (
                            <circle
                                cx="40"
                                cy="40"
                                r={radius}
                                fill="none"
                                stroke="#22c55e"
                                strokeWidth="8"
                                strokeDasharray={`${syncedDash} ${circumference}`}
                                strokeDashoffset={syncedOffset}
                                className="transition-all duration-500"
                            />
                        )}
                        {/* Pending segment (yellow) */}
                        {pendingPercent > 0 && (
                            <circle
                                cx="40"
                                cy="40"
                                r={radius}
                                fill="none"
                                stroke="#f59e0b"
                                strokeWidth="8"
                                strokeDasharray={`${pendingDash} ${circumference}`}
                                strokeDashoffset={pendingOffset}
                                className="transition-all duration-500"
                            />
                        )}
                        {/* Failed segment (red) */}
                        {failedPercent > 0 && (
                            <circle
                                cx="40"
                                cy="40"
                                r={radius}
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="8"
                                strokeDasharray={`${failedDash} ${circumference}`}
                                strokeDashoffset={failedOffset}
                                className="transition-all duration-500"
                            />
                        )}
                        {/* Unknown segment (gray) */}
                        {unknownPercent > 0 && (
                            <circle
                                cx="40"
                                cy="40"
                                r={radius}
                                fill="none"
                                stroke="#9ca3af"
                                strokeWidth="8"
                                strokeDasharray={`${unknownDash} ${circumference}`}
                                strokeDashoffset={unknownOffset}
                                className="transition-all duration-500"
                            />
                        )}
                    </svg>
                    {/* Center text */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-lg font-bold text-gray-900">{Math.round(syncedPercent)}%</span>
                        <span className="text-xs text-gray-500">Synced</span>
                    </div>
                </div>

                {/* Legend */}
                <div className="flex flex-col justify-center space-y-2">
                    <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-success-500"></div>
                        <span className="text-sm text-gray-600">Synced ({data.synced})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-warning-500"></div>
                        <span className="text-sm text-gray-600">Pending ({data.pending})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-danger-500"></div>
                        <span className="text-sm text-gray-600">Failed ({data.failed})</span>
                    </div>
                    {data.unknown > 0 && (
                        <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                            <span className="text-sm text-gray-600">Unknown ({data.unknown})</span>
                        </div>
                    )}
                </div>
            </div>

            {/* All devices — unsynced first, scrollable */}
            {allDevices && allDevices.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-700">Fleet Status</h3>
                        <span className="text-xs text-gray-500">{allDevices.length} devices</span>
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {[...allDevices]
                            .sort((a, b) => {
                                // Failed first, then pending, then synced, then unknown
                                const order: Record<string, number> = { failed: 0, pending: 1, applying: 2, unknown: 3, synced: 4 };
                                return (order[a.status] ?? 3) - (order[b.status] ?? 3);
                            })
                            .map((device) => {
                                const linkPath = device.deviceType === 'connector'
                                    ? `/connectors/${device.id}`
                                    : device.deviceType === 'mtge'
                                        ? `/mtges/${device.id}`
                                        : `/edges/${device.id}`;
                                return (
                                    <div key={device.id} className="flex items-center justify-between p-1.5 rounded hover:bg-gray-50">
                                        <Link
                                            to={linkPath}
                                            className="text-xs font-medium text-gray-700 hover:text-primary-600 truncate flex-1 min-w-0"
                                            title={device.dirtyReason || device.lastError || undefined}
                                        >
                                            {device.name}
                                        </Link>
                                        <StatusBadge status={device.status} />
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfigComplianceCard;
